import { Dispatch, SetStateAction, useRef, useState } from "react"
import useSWRImmutable from "swr/immutable"

import { EVACDeviceFormData } from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { getTsIoMapper } from "../../../../../utils/general/generalUtils"
import {
    getEmptyEVACDeviceFormData,
    mapEVACDeviceResponseToDeviceFormData,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import { getClientProtected } from "../../../clients/clients"
import { NEW_VERSION_CONFIG } from "../../../swrConfig"
import { NewEvacDeviceVersionResponse } from "../../../types/devices/lct/publishEvacDeviceTypes"
import { START_NEW_LCT_DEVICE_VERSION } from "../../../urls/private"

interface IUseStartNewEVACDeviceVersionOutput {
    data: EVACDeviceFormData
    setData: Dispatch<SetStateAction<EVACDeviceFormData>>
    apiResSystemRef: string | null
    isLoading: boolean
    isValidating: boolean
    error?: AppError
}

const startNewEvacDeviceVersionMapper = getTsIoMapper(
    NewEvacDeviceVersionResponse,
    "startNewEvacDeviceVersionMapper"
)

/**
 * Hook for starting a new device version.
 * Returns data for the new device version, which is then used to populate the device context
 */
const useStartNewEVACDeviceVersion = (
    deviceRef: string,
    attachmentUUID: string
): IUseStartNewEVACDeviceVersionOutput => {
    const [data, setData] = useState<EVACDeviceFormData>(
        getEmptyEVACDeviceFormData()
    )
    const [isLoading, setIsLoading] = useState(true)

    // Only to be set in the SWR fetcher. Ref used as useState triggers the
    // re-render
    const apiResSystemRef = useRef<string | null>(null)

    const { error, isValidating } = useSWRImmutable(
        START_NEW_LCT_DEVICE_VERSION(
            deviceRef,
            attachmentUUID,
            LCTOnlyDatasets.EVAC
        ),
        async (url: string): Promise<void> => {
            try {
                const response = await getClientProtected(url)
                const mappedRes = startNewEvacDeviceVersionMapper.map(response)
                apiResSystemRef.current = mappedRes.SystemReference
                setData(mapEVACDeviceResponseToDeviceFormData(mappedRes))
            } catch {
                throw invalidApiDataError(
                    "Failed to start new EVAC version - data load failed"
                )
            } finally {
                setIsLoading(false)
            }
        },
        NEW_VERSION_CONFIG
    )

    return {
        data,
        setData,
        apiResSystemRef: apiResSystemRef.current,
        isLoading,
        isValidating,
        error,
    }
}

export default useStartNewEVACDeviceVersion
